import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  SvgIconProps,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PhonelinkEraseIcon from "@mui/icons-material/PhonelinkErase";
import { useTranslation } from "react-i18next";
import { useValidationJourneys } from "@/hooks/useTroubleshooting";
import {
  JourneyStepStatus,
  ValidationJourney,
} from "@/domain/troubleshooting.interface";
import { formatInBackofficeLondon } from "@/utils/dateUtils";
import { TimelineWindow } from "@/utils/troubleshooting.utils";
import { DEVICE_ONLY_JOURNEY_PREFIX } from "@/domain/troubleshooting.enum";
import { TIMELINE_TIME_FORMAT } from "./TimelineSwimlane";

export const STEP_STATUS_META: Record<
  JourneyStepStatus,
  { color: string; Icon: React.ComponentType<SvgIconProps> }
> = {
  DONE: { color: "#2E7D32", Icon: CheckCircleIcon },
  SKIPPED: { color: "#B26A00", Icon: RemoveCircleIcon },
  FAILED: { color: "#C62828", Icon: CancelIcon },
  PENDING: { color: "#1565C0", Icon: HourglassTopIcon },
  NOT_REACHED: { color: "#9E9E9E", Icon: RadioButtonUncheckedIcon },
};

/** "SYNC_RESULT" -> "Sync result": label fallback for keys without a translation. */
export const humanizeKey = (key: string): string => {
  const words = key.toLowerCase().split("_").filter(Boolean).join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Journey labels with a readable fallback, so a step key or journey kind the
 * backend adds later still renders without a code change.
 */
export const useJourneyLabels = () => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string, options?: Record<string, unknown>) => string;
  return {
    step: (key: string) =>
      tt(`steps.${key}`, { defaultValue: humanizeKey(key) }),
    kind: (kind: string) =>
      tt(`journeys.kind.${kind}`, { defaultValue: humanizeKey(kind) }),
    status: (status: string) =>
      tt(`stepStatus.${status}`, { defaultValue: humanizeKey(status) }),
  };
};

interface ValidationJourneysProps {
  participantId: string;
  window: TimelineWindow;
  enabled?: boolean;
  /** Journey to expand, highlight and scroll to ("Trace this event"). */
  focusJourneyId?: string;
  /** Live mode: refresh the list periodically while this tab is open. */
  live?: boolean;
  /** Live refresh period (from the participantTimeline setting). */
  liveIntervalMs?: number;
  onOpenEvent: (eventId: string) => void;
}

const JourneySteps: React.FC<{
  journey: ValidationJourney;
  onOpenEvent: (eventId: string) => void;
}> = ({ journey, onOpenEvent }) => {
  const { t } = useTranslation("troubleshooting");
  const label = useJourneyLabels();
  // Generic: the steps are rendered exactly as the API orders them, whatever
  // the journey kind (contract §4 / §6.4).
  return (
    <Box
      component="ol"
      aria-label={label.kind(journey.kind)}
      sx={{ listStyle: "none", p: 0, m: 0 }}
    >
      {journey.steps.map((step, index) => {
        const { color, Icon } =
          STEP_STATUS_META[step.status] || STEP_STATUS_META.NOT_REACHED;
        const stopped = journey.stoppedAt === step.key;
        return (
          <Box
            component="li"
            key={step.key}
            data-testid={`journey-step-${step.key}`}
            data-stopped={stopped || undefined}
            aria-current={stopped ? "step" : undefined}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              p: 1.5,
              mb: 1,
              borderRadius: 1,
              border: "1px solid",
              borderColor: stopped ? color : "divider",
              borderLeftWidth: stopped ? 6 : 1,
              backgroundColor: stopped ? `${color}14` : "transparent",
              opacity: step.status === "NOT_REACHED" ? 0.7 : 1,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 20, fontWeight: 700 }}
            >
              {index + 1}
            </Typography>
            <Icon aria-hidden sx={{ color }} fontSize="small" />
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {label.step(step.key)}
                </Typography>
                <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
                  {label.status(step.status)}
                </Typography>
                {stopped && (
                  <Chip
                    size="small"
                    label={t("journeys.stoppedHere")}
                    sx={{ backgroundColor: color, color: "#fff" }}
                  />
                )}
              </Box>
              {step.at && (
                <Typography variant="caption" color="text.secondary">
                  {formatInBackofficeLondon(step.at, TIMELINE_TIME_FORMAT)}
                </Typography>
              )}
              {step.reason && (
                <Typography variant="body2">{step.reason}</Typography>
              )}
              {stopped && journey.stopReason && (
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, mt: 0.5 }}
                  data-testid="journey-stop-reason"
                >
                  {journey.stopReason}
                </Typography>
              )}
            </Box>
            {step.eventId && (
              <Button
                size="small"
                onClick={() => onOpenEvent(step.eventId as string)}
              >
                {t("journeys.viewEvent")}
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const ValidationJourneys: React.FC<ValidationJourneysProps> = ({
  participantId,
  window,
  enabled = true,
  focusJourneyId,
  live = false,
  liveIntervalMs,
  onOpenEvent,
}) => {
  const { t } = useTranslation("troubleshooting");
  const tt = t as (key: string, options?: Record<string, unknown>) => string;
  const label = useJourneyLabels();
  const {
    data: journeys,
    isLoading,
    isError,
    refetch,
  } = useValidationJourneys(participantId, window.from, window.to, {
    enabled,
    live,
    liveIntervalMs,
  });

  // Expansion is controlled so a traced journey opens even when the list is
  // already on screen. Default: the traced journey, else the first one.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const isExpanded = (id: string, index: number) =>
    expanded[id] ?? (focusJourneyId ? id === focusJourneyId : index === 0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focusJourneyId) return;
    setExpanded((prev) => ({ ...prev, [focusJourneyId]: true }));
    const node = Array.from(
      containerRef.current?.querySelectorAll<HTMLElement>(
        "[data-journey-id]",
      ) || [],
    ).find((el) => el.dataset.journeyId === focusJourneyId);
    node?.scrollIntoView?.({ block: "nearest" });
  }, [focusJourneyId, journeys]);

  return (
    <Box ref={containerRef}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("journeys.hint")}
      </Typography>

      {isLoading && (
        <Box>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t("journeys.loading")}
          </Typography>
        </Box>
      )}

      {isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {t("timeline.retry")}
            </Button>
          }
        >
          {t("journeys.error")}
        </Alert>
      )}

      {journeys && journeys.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t("journeys.empty")}
        </Typography>
      )}

      {journeys?.map((journey, index) => {
        const stoppedStatus = journey.steps.find(
          (s) => s.key === journey.stoppedAt,
        )?.status;
        const stoppedMeta = journey.stoppedAt
          ? (stoppedStatus && STEP_STATUS_META[stoppedStatus]) ||
            STEP_STATUS_META.FAILED
          : null;
        const focused = journey.id === focusJourneyId;
        const deviceOnly = journey.id.startsWith(DEVICE_ONLY_JOURNEY_PREFIX);
        return (
          <Accordion
            key={journey.id}
            variant="outlined"
            disableGutters
            expanded={isExpanded(journey.id, index)}
            onChange={(_, value) =>
              setExpanded((prev) => ({ ...prev, [journey.id]: value }))
            }
            sx={{
              mb: 1,
              ...(focused && {
                borderColor: "primary.main",
                boxShadow: "0 0 0 2px rgba(98,83,162,0.35)",
              }),
            }}
            TransitionProps={{ unmountOnExit: true }}
            data-testid={`journey-${journey.id}`}
            data-journey-id={journey.id}
            data-focused={focused || undefined}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>
                  {label.kind(journey.kind)}
                </Typography>
                {deviceOnly && (
                  <Tooltip title={t("journeys.deviceOnlyHint")}>
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      icon={<PhonelinkEraseIcon aria-hidden />}
                      label={t("journeys.deviceOnly")}
                      data-testid="journey-device-only"
                    />
                  </Tooltip>
                )}
                {focused && (
                  <Chip
                    size="small"
                    color="primary"
                    label={t("journeys.focused")}
                  />
                )}
                <Typography variant="body2" color="text.secondary">
                  {tt("journeys.startedAt", {
                    time: formatInBackofficeLondon(
                      journey.startedAt,
                      TIMELINE_TIME_FORMAT,
                    ),
                  })}
                </Typography>
                {journey.stoppedAt && stoppedMeta ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={
                      <stoppedMeta.Icon
                        aria-hidden
                        sx={{ color: `${stoppedMeta.color} !important` }}
                      />
                    }
                    label={tt("journeys.stoppedAt", {
                      step: label.step(journey.stoppedAt),
                    })}
                    sx={{
                      borderColor: stoppedMeta.color,
                      color: stoppedMeta.color,
                    }}
                  />
                ) : (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="success"
                    icon={<CheckCircleIcon aria-hidden />}
                    label={t("journeys.completed")}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <JourneySteps journey={journey} onOpenEvent={onOpenEvent} />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default ValidationJourneys;
